import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { changeMyPassword, requestPasswordChangeCode, userProfileError } from '../../services/apiUserProfile.ts';
import { endBrowserSessionAfterPasswordChange } from '../../services/api.ts';
import { getSafeMcpReturnTo } from '../../utils/mcpAuthorization.ts';

export const PasswordChangeForm: React.FC<{ requiredChange?: boolean }> = ({ requiredChange = false }) => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const codeInput = useRef<HTMLInputElement>(null);
  const focusCode = useRef(false);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState('');
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!resendAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [resendAt]);
  const cooldown = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const sendCode = async () => {
    let result;
    try { result = await requestPasswordChangeCode({ current_password: current }); }
    catch (err) {
      setChallengeId(''); setCode(''); setNotice(''); focusCode.current = false;
      const retryAfter = Number((err as { response?: { headers?: Record<string, string> } })?.response?.headers?.['retry-after']);
      if (Number.isFinite(retryAfter) && retryAfter > 0) { setNow(Date.now()); setResendAt(Date.now() + retryAfter * 1000); }
      throw err;
    }
    focusCode.current = true;
    setChallengeId(result.challenge_id); setCode('');
    setNow(Date.now()); setResendAt(Date.now() + result.resend_after * 1000);
    setNotice(result.message || 'Código enviado ao seu e-mail cadastrado.');
  };
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  useEffect(() => {
    if (!busy && challengeId && focusCode.current) { codeInput.current?.focus(); focusCode.current = false; }
  }, [busy, challengeId]);
  const inputClass = `mt-1 w-full rounded-xl border p-3 ${isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`;
  return <form className="space-y-4" onSubmit={async e => {
    e.preventDefault(); if (submitting.current || (!challengeId && cooldown > 0)) return;
    setError('');
    if (password.length < 12 || new TextEncoder().encode(password).length > 72) { setError('Use uma nova senha com pelo menos 12 caracteres e no máximo 72 bytes.'); return; }
    if (password !== confirmation) { setError('As senhas não conferem.'); return; }
    submitting.current = true; setBusy(true);
    try {
      if (!challengeId) { await sendCode(); return; }
      if (!/^\d{6}$/.test(code)) { setError('Informe o código de 6 dígitos enviado ao seu e-mail.'); return; }
      await changeMyPassword({ current_password: current, new_password: password, confirm_password: confirmation, challenge_id: challengeId, verification_code: code });
      setCurrent(''); setPassword(''); setConfirmation('');
      localStorage.setItem('auth_notice_message', 'Senha alterada. Entre novamente com sua nova senha.');
      endBrowserSessionAfterPasswordChange();
      const returnTo = getSafeMcpReturnTo(location.search);
      navigate(returnTo ? `/?returnTo=${encodeURIComponent(returnTo)}` : '/', { replace: true });
    } catch (err) { setError(userProfileError(err, 'Não foi possível alterar a senha.')); }
    finally { submitting.current = false; setBusy(false); }
  }}>
    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{requiredChange ? 'Defina sua senha pessoal para continuar. A senha temporária será substituída.' : 'Ao alterar sua senha, será necessário entrar novamente em todos os dispositivos.'}</p>
    <label className="block text-sm">{requiredChange ? 'Senha temporária' : 'Senha atual'}<input aria-label={requiredChange ? 'Senha temporária' : 'Senha atual'} type="password" autoComplete="current-password" required disabled={busy} value={current} onChange={e => { setCurrent(e.target.value); setChallengeId(''); setCode(''); setNotice(''); }} className={inputClass} /></label>
    <label className="block text-sm">Nova senha<input aria-label="Nova senha" type="password" autoComplete="new-password" minLength={12} required disabled={busy} value={password} onChange={e => setPassword(e.target.value)} className={inputClass} /></label>
    <label className="block text-sm">Confirmar nova senha<input aria-label="Confirmar nova senha" type="password" autoComplete="new-password" required disabled={busy} value={confirmation} onChange={e => setConfirmation(e.target.value)} className={inputClass} /></label>
    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Use pelo menos 12 caracteres.</p>
    {notice && <p role="status" className="text-sm text-sminy-primary">{notice}</p>}
    {challengeId && <div className="space-y-2">
      <label className="block text-sm">Código de verificação<input ref={codeInput} aria-label="Código de verificação" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required disabled={busy} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} className={inputClass} /></label>
      <p className="text-xs">Use o código enviado ao seu e-mail cadastrado. Ele expira em 10 minutos.</p>
      <button type="button" disabled={busy || cooldown > 0} className="text-sm text-sminy-primary disabled:opacity-50" onClick={async () => {
        if (submitting.current || cooldown > 0) return;
        submitting.current = true; setBusy(true); setError('');
        try { await sendCode(); } catch (err) { setError(userProfileError(err, 'Não foi possível reenviar o código.')); }
        finally { submitting.current = false; setBusy(false); }
      }}>{cooldown > 0 ? `Reenviar código em ${cooldown}s` : 'Reenviar código'}</button>
    </div>}
    {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
    <button type="submit" disabled={busy || (!challengeId && cooldown > 0)} className="rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Aguarde...' : challengeId ? 'Salvar nova senha' : cooldown > 0 ? `Enviar código em ${cooldown}s` : 'Enviar código por e-mail'}</button>
  </form>;
};
